import React, { Suspense, lazy, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthDestination, DESTINATION, DESTINATION_PATH } from '../hooks/useAuthDestination';
import AutomationOverlay from './AutomationOverlay';

// Hold the document still for as long as the caller is mounted, restoring
// whatever was there before. Exported because App.js's RouteSplash — the OTHER
// thing that can be the only thing on screen while the app boots — needs the
// identical behavior, and two copies of a body-style mutation is how one of
// them ends up not restoring.
//
// COUNTED, NOT PAIRED, and that is not defensive coding — the two splashes are
// GENUINELY on screen together. Measured on /login with the page chunk held:
// two .bf-viewport-fill elements, LoadingScreen hidden at display:none by
// Suspense and RouteSplash visible at 812px. The naive save/restore pair
// deadlocks on exactly that overlap: the second lock saves 'hidden' as the
// value to go back to, the first unmounts and restores '', and the second's
// unmount then puts 'hidden' back with nothing on screen — an app that can
// never scroll again. Only the LAST holder out restores, and it restores what
// the FIRST one found.
let scrollLocks = 0;
let scrollLockPrev = '';

export function useScrollLock() {
  useEffect(() => {
    if (scrollLocks === 0) {
      scrollLockPrev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    scrollLocks += 1;
    return () => {
      scrollLocks -= 1;
      if (scrollLocks === 0) document.body.style.overflow = scrollLockPrev;
    };
  }, []);
}

// LAZY, unlike ProtectedRoute itself. The reason App.js keeps this file a
// static import — that a lazy PARENT route puts a serial chunk fetch in front
// of the child page's own — does not apply to a SIBLING of the Outlet: this
// chunk and the Dashboard's are requested in parallel, and the fallback is
// nothing at all. So the launcher (plus its capture and upload code) stays out
// of main.js, where it would otherwise ship to every signed-out visitor on the
// marketing page, who can never use it.
const BugReportWidget = lazy(() => import('./BugReportWidget'));

// Brand splash shown while auth/company state resolves. Shared with the
// /login and /onboarding routes in App.js.
//
// NOTHING HERE MOVES. There is one word on screen and no content below it, so
// any movement at all is the surface feeling loose — the same standard round 4
// set for sheets ("only content that is meant to scroll may move"). Three
// things were letting it move, and all three are needed:
//
//   • `minHeight: 100vh` is the LARGE viewport in an iOS Safari tab — taller
//     than the visible screen by the height of the toolbar, so the splash was
//     genuinely a scrollable page. bf-viewport-fill is dvh with the vh
//     fallback, i.e. exactly what is visible. Safe here for the reason the
//     index.css note gives: this surface locks the scroll, so the toolbar
//     cannot collapse underneath it and re-resolve the unit.
//   • the body scroll lock, the same one BottomSheet takes, so no ancestor can
//     scroll while the splash is up.
//   • overscroll-behavior: none, which is the only one of the three that stops
//     iOS RUBBER-BANDING a page that has nothing to scroll. Chromium has no
//     rubber band, so this part is reasoned, not photographed.
export function LoadingScreen() {
  useScrollLock();
  return (
    <div className="bf-viewport-fill" style={{
      backgroundColor: localStorage.getItem('boneflow_theme') === 'night' ? '#0e0d0b' : '#f4f1eb',
      overflow: 'hidden',
      overscrollBehavior: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <p style={{
        fontSize: '10px',
        color: '#3d3d3d',
        textTransform: 'uppercase',
        letterSpacing: '3px',
        fontWeight: '600',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
      }}>
        BoneFlow
      </p>
    </div>
  );
}

// Gate for every authenticated route. The decision itself lives in
// useAuthDestination — the ONE place that answers "where does this session
// belong", shared with the /, /login and /onboarding gates in App.js.
export default function ProtectedRoute() {
  const destination = useAuthDestination();
  const location = useLocation();

  if (destination === DESTINATION.PENDING) return <LoadingScreen />;

  // CARRY THE DESTINATION THROUGH THE LOGIN. Every deep link in a notification
  // email lands on one of these routes (/tasks, /workflows/:jobId,
  // /clients/:clientId/jobs/:jobId), and a recipient reading mail on a phone is
  // exactly the person least likely to already have a session there. Without
  // this, "View Job" meant "go to the dashboard and find it yourself".
  if (destination === DESTINATION.LOGIN) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (destination === DESTINATION.ONBOARDING) return <Navigate to="/onboarding" replace />;
  // The account gate: an unconfirmed email, or a company not yet let in, sees
  // its own calm screen instead of any app page. Deep links included — a
  // pending account following /workflows/abc lands on the review screen.
  if (destination === DESTINATION.VERIFY_EMAIL || destination === DESTINATION.ACCOUNT_REVIEW) {
    return <Navigate to={DESTINATION_PATH[destination]} replace />;
  }

  // THE APP SHELL for every authenticated route. Only <Outlet/> changes as the
  // user navigates — this element is the parent route's, so anything rendered
  // beside the outlet mounts exactly ONCE for the whole signed-in session and
  // survives every client-side navigation without remounting. AutomationOverlay
  // has always relied on that; the bug reporter is mounted the same way, which
  // is what makes it present on every page without any page knowing about it.
  return (
    <>
      <Outlet />
      <AutomationOverlay />
      <Suspense fallback={null}>
        <BugReportWidget />
      </Suspense>
    </>
  );
}
